import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, interval, merge, timer } from 'rxjs';
import { catchError, map, startWith, switchMap, take } from 'rxjs/operators';
import {
  CreateCustomsCalculationRequest,
  CreateCustomsProductRequest,
  CustomsCalculationsService,
  InvoiceProcessingStatus
} from './api/customs-calculations';
import { InvoiceSocketService } from './api/invoice-socket';

/** Ход создания расчёта: сообщения сервера по порядку, признак завершения и ошибка. */
export interface CalcProgress {
  id: number;
  createdAt?: string;
  log: string[];
  done: boolean;
  error: string | null;
}

export interface CalcProgressTexts {
  first: string;
  invoiceSaved?: string;
  processingError: string;
  addProductError?: string;
}

/**
 * Создание расчёта и ИИ-разбор товаров живут здесь, а не на странице «Новый расчёт»:
 * как только сервер выдал номер, клиент сразу переходит в кабинет расчёта и видит ход работы там,
 * а запросы при этом не обрываются.
 */
@Injectable({ providedIn: 'root' })
export class CalculationProgressService {
  private states = new Map<number, BehaviorSubject<CalcProgress>>();
  private watchers = new Map<number, Subscription>();
  /** Любое изменение состояния — для бокового меню. */
  readonly changed$ = new Subject<number>();

  constructor(
    private customsCalculationsService: CustomsCalculationsService,
    private invoiceSocket: InvoiceSocketService
  ) {}

  /** Расчёт ещё обрабатывается (есть незавершённый ход без ошибки). */
  isActive(id: number): boolean {
    const s = this.states.get(id)?.value;
    return !!s && !s.done && !s.error;
  }

  watch(id: number): Observable<CalcProgress | null> {
    const state = this.states.get(id);
    return state ? state.asObservable() : new BehaviorSubject<CalcProgress | null>(null).asObservable();
  }

  /** Загрузка инвойса: отдаёт номер расчёта, как только сервер его создал. */
  startInvoice(file: File, title: string | undefined, texts: CalcProgressTexts): Observable<number> {
    return this.customsCalculationsService.createCalculationFromInvoice(file, title).pipe(
      map(calculation => {
        this.init(calculation.id, calculation.createdAt, texts.first);
        this.push(calculation.id, texts.invoiceSaved);
        this.watchInvoice(calculation.id, texts);
        return calculation.id;
      })
    );
  }

  /** Ручной ввод: создаём расчёт и по очереди добавляем товары (каждый классифицирует ИИ). */
  startManual(calculation: CreateCustomsCalculationRequest, products: CreateCustomsProductRequest[], texts: CalcProgressTexts): Observable<number> {
    return this.customsCalculationsService.createCalculation(calculation).pipe(
      map(created => {
        this.init(created.id, created.createdAt, texts.first);
        if (products.length === 0) {
          this.finish(created.id);
        } else {
          this.watchClassification(created.id);
          this.addProducts(created.id, products, 0, texts);
        }
        return created.id;
      })
    );
  }

  private init(id: number, createdAt: string | undefined, first: string): void {
    this.states.set(id, new BehaviorSubject<CalcProgress>({ id, createdAt, log: first ? [first] : [], done: false, error: null }));
    this.changed$.next(id);
  }

  /** Новый шаг; повтор того же сообщения (опрос раз в 2,5 с) не дублируем. */
  private push(id: number, message: string | null | undefined): void {
    const state = this.states.get(id);
    const text = (message ?? '').trim();
    if (!state || !text || state.value.log[state.value.log.length - 1] === text) return;
    state.next({ ...state.value, log: [...state.value.log, text] });
  }

  private finish(id: number, error: string | null = null): void {
    const state = this.states.get(id);
    this.watchers.get(id)?.unsubscribe();
    this.watchers.delete(id);
    if (!state || state.value.done) return;
    state.next({ ...state.value, done: !error, error });
    this.changed$.next(id);
  }

  private watchInvoice(id: number, texts: CalcProgressTexts): void {
    const ws$ = this.invoiceSocket.watch(id).pipe(catchError(() => EMPTY));
    const poll$ = interval(2500).pipe(
      startWith(0),
      switchMap(() => this.customsCalculationsService.getInvoiceProcessingStatus(id).pipe(catchError(() => EMPTY)))
    );
    const sub = merge(ws$, poll$).subscribe((status: InvoiceProcessingStatus) => {
      if (!status || !this.isActive(id)) return;
      this.push(id, status.message);
      if (status.status === 'DONE') {
        this.finish(id);
      } else if (status.status === 'ERROR') {
        this.finish(id, status.error || status.message || texts.processingError);
      }
    });
    // Страховка: через 15 минут открываем расчёт как есть
    sub.add(timer(15 * 60 * 1000).pipe(take(1)).subscribe(() => this.finish(id)));
    this.watchers.set(id, sub);
  }

  /** При ручном вводе сервер присылает, как ИИ понял товар и какой код нашёл. */
  private watchClassification(id: number): void {
    const sub = this.invoiceSocket.watch(id).pipe(catchError(() => EMPTY)).subscribe(status => {
      if (status?.status === 'PROCESSING' && status.message) {
        this.push(id, status.message);
      }
    });
    this.watchers.set(id, sub);
  }

  private addProducts(id: number, products: CreateCustomsProductRequest[], index: number, texts: CalcProgressTexts): void {
    if (index >= products.length) {
      this.finish(id);
      return;
    }
    this.customsCalculationsService.addProduct(id, products[index]).subscribe({
      next: () => this.addProducts(id, products, index + 1, texts),
      error: () => {
        // Один товар не добавился — продолжаем с остальными, ошибку покажем в ходе расчёта
        this.push(id, texts.addProductError);
        this.addProducts(id, products, index + 1, texts);
      }
    });
  }
}
